/* =========================================================================
   MARKETING Hero — Module registry (the 24 main-menu modules).
   `status` reflects the milestone rollout: "ready" = usable now,
   "building" = scaffolded, filled in a later milestone. Shared by the
   sidebar, the dashboard quick actions, and routing.
   ========================================================================= */

export type ModuleStatus = "ready" | "building";

export interface ModuleDef {
  id: string;
  path: string;
  label: string; // Thai
  en: string; // English term
  icon: string; // lucide-react icon name
  group: string;
  milestone: number;
  status: ModuleStatus;
  desc: string;
}

export const MODULE_GROUPS = [
  "ภาพรวม",
  "แบรนด์ & ลูกค้า",
  "ผู้ช่วย AI",
  "วิเคราะห์ลูกค้า & กลุ่มเป้าหมาย",
  "คอนเทนต์ & ครีเอทีฟ",
  "โฆษณา",
  "คอนเวอร์ชัน",
  "ความรู้ & รายงาน",
  "ตั้งค่า",
] as const;

export const MODULES: ModuleDef[] = [
  { id: "dashboard", path: "/", label: "แดชบอร์ด", en: "Dashboard", icon: "LayoutDashboard", group: "ภาพรวม", milestone: 1, status: "ready", desc: "ภาพรวม + Quick Actions" },

  { id: "brands", path: "/brands", label: "แบรนด์ & ลูกค้า", en: "Brands & Clients", icon: "Building2", group: "แบรนด์ & ลูกค้า", milestone: 1, status: "ready", desc: "จัดการแบรนด์ ลูกค้า และ Brand Brain" },

  { id: "advisor", path: "/advisor", label: "AI Marketing Advisor", en: "AI Advisor", icon: "Sparkles", group: "ผู้ช่วย AI", milestone: 2, status: "ready", desc: "ถาม-ตอบ วิเคราะห์ ขอ Second Opinion" },
  { id: "strategy", path: "/strategy", label: "วางกลยุทธ์การตลาด", en: "Marketing Strategy", icon: "Target", group: "ผู้ช่วย AI", milestone: 2, status: "ready", desc: "Strategy Wizard + 30-Day Plan" },

  { id: "audience", path: "/audience", label: "วิเคราะห์กลุ่มเป้าหมาย", en: "Audience Intelligence", icon: "Users", group: "วิเคราะห์ลูกค้า & กลุ่มเป้าหมาย", milestone: 3, status: "ready", desc: "Persona · Pain · Desire · Objection" },
  { id: "interest", path: "/interest", label: "Facebook Interest", en: "Interest Intelligence", icon: "Radar", group: "วิเคราะห์ลูกค้า & กลุ่มเป้าหมาย", milestone: 3, status: "ready", desc: "Audience Discovery Engine + Clusters" },

  { id: "content", path: "/content", label: "คิดคอนเทนต์", en: "Content Studio", icon: "PenLine", group: "คอนเทนต์ & ครีเอทีฟ", milestone: 5, status: "ready", desc: "Content Pillar · Plan · Hook · Caption" },
  { id: "creative", path: "/creative", label: "ออกแบบครีเอทีฟ", en: "Creative Studio", icon: "Image", group: "คอนเทนต์ & ครีเอทีฟ", milestone: 5, status: "ready", desc: "Creative Brief · Angles · Review" },
  { id: "video", path: "/video", label: "ทำวิดีโอ", en: "Video Studio", icon: "Clapperboard", group: "คอนเทนต์ & ครีเอทีฟ", milestone: 5, status: "ready", desc: "Script · Storyboard · Shot list" },

  { id: "ads", path: "/ads", label: "ศูนย์วิเคราะห์แอด", en: "Ads Center", icon: "Megaphone", group: "โฆษณา", milestone: 4, status: "building", desc: "Meta · Google · TikTok (manual data)" },
  { id: "ads-doctor", path: "/ads-doctor", label: "Ads Doctor", en: "Ads Doctor", icon: "Stethoscope", group: "โฆษณา", milestone: 4, status: "ready", desc: "วินิจฉัยปัญหาแอดเชิงลึก" },
  { id: "second-opinion", path: "/second-opinion", label: "ขอ Second Opinion", en: "Second Opinion", icon: "Scale", group: "โฆษณา", milestone: 4, status: "ready", desc: "AI ท้าทายแผนของคุณ" },
  { id: "scaling", path: "/scaling", label: "วางแผน Scale", en: "Scaling Advisor", icon: "TrendingUp", group: "โฆษณา", milestone: 4, status: "ready", desc: "Scale · Hold · Kill decision" },

  { id: "sales-page", path: "/sales-page", label: "สร้าง Sales Page", en: "Sales Page Engine", icon: "FileText", group: "คอนเวอร์ชัน", milestone: 6, status: "ready", desc: "5-Layer conversion workflow" },
  { id: "funnel", path: "/funnel", label: "วาง Funnel", en: "Funnel Architect", icon: "Filter", group: "คอนเวอร์ชัน", milestone: 7, status: "building", desc: "Funnel builder + Leak analysis" },
  { id: "competitor", path: "/competitor", label: "วิเคราะห์คู่แข่ง", en: "Competitor Intelligence", icon: "Swords", group: "คอนเวอร์ชัน", milestone: 7, status: "building", desc: "จุดแข็ง จุดอ่อน ช่องว่าง" },

  { id: "journal", path: "/journal", label: "บันทึกแคมเปญ", en: "Campaign Journal", icon: "NotebookPen", group: "ความรู้ & รายงาน", milestone: 8, status: "building", desc: "Timeline + ผลลัพธ์จริง" },
  { id: "playbook", path: "/playbook", label: "Sooksabay Playbook", en: "Playbook", icon: "BookMarked", group: "ความรู้ & รายงาน", milestone: 8, status: "building", desc: "Industry presets + Winning patterns" },
  { id: "knowledge", path: "/knowledge", label: "คลังความรู้", en: "Knowledge Base", icon: "Library", group: "ความรู้ & รายงาน", milestone: 8, status: "building", desc: "Learning ที่ระบบสะสม" },
  { id: "coach", path: "/coach", label: "เตรียมการสอน", en: "Coach Mode", icon: "GraduationCap", group: "ความรู้ & รายงาน", milestone: 9, status: "building", desc: "Lesson plan · Workshop (McAlive)" },
  { id: "reports", path: "/reports", label: "รายงาน", en: "Reports", icon: "BarChart3", group: "ความรู้ & รายงาน", milestone: 9, status: "building", desc: "Daily · Weekly · Monthly · Client" },

  { id: "settings", path: "/settings", label: "ตั้งค่า", en: "Settings", icon: "Settings", group: "ตั้งค่า", milestone: 1, status: "ready", desc: "AI Provider · API Key · Workspace" },
];

/** Dashboard "วันนี้อยากให้ AI ช่วยอะไร?" quick actions (order matters). */
export const QUICK_ACTIONS: { label: string; en: string; icon: string; path: string }[] = [
  { label: "วิเคราะห์ Ads", en: "Analyze Ads", icon: "Megaphone", path: "/ads" },
  { label: "Ads Doctor", en: "Ads Doctor", icon: "Stethoscope", path: "/ads-doctor" },
  { label: "หา Facebook Interest", en: "Find Interest", icon: "Radar", path: "/interest" },
  { label: "วาง Campaign", en: "Plan Campaign", icon: "Megaphone", path: "/ads" },
  { label: "ขอ Second Opinion", en: "Second Opinion", icon: "Scale", path: "/second-opinion" },
  { label: "วาง Marketing Strategy", en: "Strategy", icon: "Target", path: "/strategy" },
  { label: "คิด Content", en: "Content", icon: "PenLine", path: "/content" },
  { label: "สร้าง Creative", en: "Creative", icon: "Image", path: "/creative" },
  { label: "ทำ Video", en: "Video", icon: "Clapperboard", path: "/video" },
  { label: "สร้าง Sales Page", en: "Sales Page", icon: "FileText", path: "/sales-page" },
  { label: "ตรวจ Sales Page", en: "Review Page", icon: "FileText", path: "/sales-page" },
  { label: "วาง Funnel", en: "Funnel", icon: "Filter", path: "/funnel" },
  { label: "วิเคราะห์คู่แข่ง", en: "Competitor", icon: "Swords", path: "/competitor" },
  { label: "เตรียมการสอน", en: "Teach", icon: "GraduationCap", path: "/coach" },
];

export function moduleByPath(path: string): ModuleDef | undefined {
  return MODULES.find((m) => m.path === path);
}
